import { Interstitial } from '@sd/prism';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import styled, { CSSProperties } from 'styled-components';
import { FocusLock } from '../FocusLock/FocusLock';

type Props = {
  children?: React.ReactNode;
  loading: boolean | undefined;
  fullScreen?: boolean | undefined;
  style?: CSSProperties;
  /**
   * If isolated is true, the loading progress will be narrowed
   * to show the spinner only in the location of the current Loader instead
   * of cascading up to the top-level loader
   */
  isolated?: true;
};

type LoadingContextObject = {
  loadingCount: number;
  startLoading: () => void;
  endLoading: () => void;
};
const LoadingContext = createContext<LoadingContextObject | undefined>(undefined);

/**
 * Display a loading spinner. These may be nested infinitely. If multiple spinners are
 * rendered on a page, the top-most spinner will always be the one to display.
 *
 * To disable the nesting feature, set the `isolated` flag to true.
 */
export const Loader = ({ children, style, loading = false, isolated, fullScreen = false }: Props) => {
  const [loadingCount, setLoadingCount] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  let loadingContext = useContext(LoadingContext);
  const isTopLevel = !loadingContext;

  loadingContext = loadingContext || {
    loadingCount,
    startLoading: () => setLoadingCount(prevCount => prevCount + 1),
    endLoading: () => setLoadingCount(prevCount => prevCount - 1)
  };

  useEffect(() => {
    if (loading) {
      loadingContext!.startLoading();
    } else {
      if (!initialLoad) loadingContext!.endLoading();
    }
    if (!initialLoad) {
      setInitialLoad(true);
    }
    return () => {
      if (loading) loadingContext!.endLoading();
    };
  }, [loading]);

  if (isolated) {
    return (
      <InternalLoader style={style} loading={loading}>
        {children}
      </InternalLoader>
    );
  }

  return isTopLevel ? (
    <LoadingContext.Provider value={loadingContext}>
      <InternalLoader style={style} fullScreen={fullScreen} loading={!!loadingCount}>
        {children}
      </InternalLoader>
    </LoadingContext.Provider>
  ) : (
    <>{children || false}</>
  );
};

const InternalLoader = ({
  fullScreen = false,
  loading,
  children,
  style
}: {
  fullScreen?: boolean;
  style?: CSSProperties;
  loading: boolean;
  children?: React.ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const loaderProps = {
    'aria-busy': true,
    'data-testid': loading ? 'loading' : 'not-loading',
    fullScreen,
    $loading: loading,
    ref: containerRef,
    role: 'progressbar',
    style
  };

  return (
    <LoaderContainer {...loaderProps}>
      <>
        {loading && (
          <>
            {/* This overlay prevents the user from manipulating the screen while the page is loading */}
            <ScreenProtector />
            {containerRef.current && <Interstitial displayDelay={0} target={containerRef} isActive />}
            <FocusLock />
          </>
        )}
        {children}
      </>
    </LoaderContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface LoadingDiv {
  fullScreen: boolean;
  $loading: boolean;
}

const LoaderContainer = styled.div<LoadingDiv>`
  position: ${props => (props.fullScreen && props.$loading ? 'fixed !important;' : 'relative;')}
  ${props => (props.fullScreen && props.$loading ? 'top: 0;' : '')}
  ${props => (props.fullScreen && props.$loading ? 'bottom: 0;' : '')}
  ${props => (props.fullScreen && props.$loading ? 'width: 100%;' : '')}
  min-height: 100px;
`;

const ScreenProtector = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
  z-index: 1;
`;
